import onetime from "@-/com.npmjs.onetime";

import type { GraphemeTaiwanTbclSet, GraphemeTaiwanUsageMap } from "#taiwan-usage-types";
import taiwanTbclGlyphs from "#taiwan-tbcl-set.gen.json" with { type: "json" };
import taiwanUsageMapData from "#taiwan-usage-map.gen.json" with { type: "json" };

/**
 * The per-glyph Taiwan MOE usage map, precomputed at static-DB build time into
 * `taiwan-usage-map.gen.json` and read synchronously here. This is fully determined by the static
 * dataset (the three MOE charts) and never depends on the user, so the device reads the bundled
 * JSON instead of running `loadTaiwanUsageMap`'s whole-`grapheme`-table scan on startup — that
 * query cost ~2.2s of JS-thread time on a slow device and was prefetched eagerly by the
 * static-database warmer. Mirrors `getComponentPrevalenceLookups` (see `-build-taiwan-usage-map.ts`).
 *
 * Kept in its own module (out of `usage-classification.ts`) so the ~700KB generated JSON only
 * enters the client bundle via the package's `.` entry — the `./server` entry, which the test seed
 * route uses, exports just the DB-backed `loadTaiwanUsageMap` and stays free of this asset.
 */
export const getTaiwanUsageMap = onetime(
  // The generated dictionary is already exclusively read through this getter. Freeze
  // it in place so Metro's module cache and glyph readers share the same entries.
  (): GraphemeTaiwanUsageMap => Object.freeze(taiwanUsageMapData),
);

/**
 * The set of traditional glyphs carrying a TBCL level, precomputed alongside the usage map into
 * `taiwan-tbcl-set.gen.json`. Paired with {@link getTaiwanUsageMap} by
 * `taiwanUsageClassificationBand` to separate the mainstream 常用字 (`common`) from the
 * chart-listed-but-untaught ones (`rare`).
 */
export const getTaiwanTbclSet = onetime((): GraphemeTaiwanTbclSet =>
  Object.freeze(new Set(taiwanTbclGlyphs)),
);
